import { Alert, Box, Button, Grid, Skeleton, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import DynamicFormField from 'src/shared/components/form/dynamicFormField';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { getFormSettingPreview } from 'src/shared/services/memberFormSettingsService';
import { getAllWebForms, getWebFormByName } from 'src/shared/services/webFormService';
import { FormItemResponse } from 'src/modules/member/models/formMemberResponse';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useGlobals } from 'src/shared/contexts/appContext';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import EmailIcon from '@mui/icons-material/Email';

type OnboardingStepWebFormProps = {
  onNavigateAway?: () => void;
};

export const OnboardingStepWebForm = ({ onNavigateAway }: OnboardingStepWebFormProps) => {
  const { t } = useTranslation(['onboarding', 'webforms']);
  const { currentUser } = useGlobals();
  const isMounted = useIsMounted();
  const [previewItems, setPreviewItems] = useState<FormItemResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [mailSubject, setMailSubject] = useState('');
  const [mailBody, setMailBody] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Hämta första webformens preview och mailinställningar
      const webFormsRes = await getAllWebForms({ filter: 'webform', pageIndex: 0, pageSize: 1, sortCol: 'formName', sortDir: 'asc' });
      const webForms = webFormsRes.data.results || [];
      const formName = webForms.length > 0 ? webForms[0].formName : 'webform';

      const [previewRes, webFormRes] = await Promise.all([getFormSettingPreview(formName), getWebFormByName(formName)]);

      if (isMounted()) {
        setPreviewItems(previewRes.data.items || []);

        const settings = webFormRes.data.settings || [];
        const subject = settings.find((s) => s.keyName === 'subjectSender');
        const body = settings.find((s) => s.keyName === 'bodySender');
        setMailSubject(subject?.varchar || '');
        setMailBody(body?.text || '');
      }
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [isMounted]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const previewFormMethods = useForm({
    defaultValues: { items: previewItems.map((item) => ({ value: item.value || item.formValue || '' })) },
  });

  const previewRows = previewItems.reduce((acc, item) => {
    const r = item.formSetting.row;
    if (!acc[r]) acc[r] = [];
    acc[r].push(item);
    return acc;
  }, {} as Record<number, FormItemResponse[]>);
  Object.values(previewRows).forEach((rowItems) => rowItems.sort((a, b) => a.formSetting.col - b.formSetting.col));

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Box sx={{ p: 2, position: 'sticky', top: 0, alignSelf: 'flex-start' }}>
            <Typography variant='body1' sx={{ mb: 2 }}>
              {t('onboarding:stepContent.webForm.description')}
            </Typography>

            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              {t('onboarding:stepContent.webForm.hint')}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={8}>
          <Typography variant='h6' fontWeight={600} sx={{ mb: 1 }}>
            {t('onboarding:stepContent.webForm.pageTitle')}
          </Typography>
          <Box sx={{ bgcolor: '#fff', borderRadius: 1, p: 2, border: 1, borderColor: 'grey.300', boxShadow: '0 2px 8px rgba(0,0,0,0.10)' }}>
            <Alert severity='warning' sx={{ mb: 2 }}>
              {t('onboarding:stepContent.webForm.editHintPrefix')}{' '}
              <Button
                variant='text'
                size='small'
                sx={{
                  p: 0,
                  minWidth: 0,
                  textTransform: 'none',
                  fontWeight: 600,
                  verticalAlign: 'baseline',
                  fontSize: 'inherit',
                  lineHeight: 'inherit',
                }}
                onClick={() => {
                  if (onNavigateAway) onNavigateAway();
                }}
              >
                {t('onboarding:stepContent.webForm.editHintLink')}
              </Button>
              . {t('onboarding:stepContent.webForm.mailHint')}
            </Alert>
            {loading ? (
              <Skeleton variant='rounded' height={200} />
            ) : (
              <Grid container spacing={2}>
                {/* Vänster: webformulär-preview */}
                <Grid item xs={12} md={6}>
                  <Typography variant='subtitle2' fontWeight={600} sx={{ mb: 1 }}>
                    {t('onboarding:stepContent.webForm.formPreviewTitle')}
                  </Typography>
                  <FormProvider {...previewFormMethods}>
                    <Box
                      sx={{
                        bgcolor: '#fff',
                        borderRadius: 1,
                        p: 2,
                        maxHeight: 400,
                        overflow: 'auto',
                        border: 1,
                        borderColor: 'grey.300',
                        '& .MuiInputBase-root': { bgcolor: '#fff' },
                      }}
                    >
                      {Object.entries(previewRows).map(([row, rowItems]) => (
                        <Box sx={{ mb: 1 }} key={row}>
                          <GridWrapper>
                            {rowItems.map((item) => {
                              const idx = previewItems.indexOf(item);
                              const safeItem = {
                                ...item,
                                value: item.value ?? item.formValue ?? '',
                                formSetting: { ...item.formSetting, fieldSize: 4 },
                              };
                              return (
                                <DynamicFormField
                                  key={`${item.formKey}-${item.formSetting.row}-${item.formSetting.col}`}
                                  formItem={safeItem}
                                  index={idx}
                                />
                              );
                            })}
                          </GridWrapper>
                        </Box>
                      ))}
                      {previewItems.length === 0 && (
                        <Typography variant='body2' color='text.secondary'>
                          {t('onboarding:stepContent.webForm.noFields')}
                        </Typography>
                      )}
                    </Box>
                  </FormProvider>
                </Grid>

                {/* Höger: mailpreview */}
                <Grid item xs={12} md={6}>
                  <Typography variant='subtitle2' fontWeight={600} sx={{ mb: 1 }}>
                    {t('onboarding:stepContent.webForm.mailPreviewTitle')}
                  </Typography>
                  <Box sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1, overflow: 'hidden' }}>
                    {/* Mail header */}
                    <Box sx={{ bgcolor: 'grey.100', px: 2, py: 1.5, borderBottom: 1, borderColor: 'grey.300' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <EmailIcon fontSize='small' color='action' />
                        <Typography variant='caption' fontWeight={600} color='text.secondary'>
                          {t('onboarding:stepContent.webForm.mailPreview')}
                        </Typography>
                      </Box>
                      <Typography variant='body2' fontWeight={600}>
                        {mailSubject || t('onboarding:stepContent.webForm.noSubject')}
                      </Typography>
                      <Typography variant='caption' color='text.secondary'>
                        {t('onboarding:stepContent.webForm.from')}: {currentUser?.customerName || ''}
                      </Typography>
                    </Box>
                    {/* Mail body */}
                    <Box sx={{ px: 2, py: 2, bgcolor: '#fff' }}>
                      {mailBody ? (
                        <Typography variant='body2' color='text.secondary' component='div' dangerouslySetInnerHTML={{ __html: mailBody }} />
                      ) : (
                        <Typography variant='body2' color='text.disabled'>
                          {t('onboarding:stepContent.webForm.noMailText')}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
